import React, { Suspense, useEffect, useState } from 'react';
import Npc from './Npc';
import { api } from '../services/api';
import { useWorldStore } from '../state/worldStore';

// NpcManager — populates the street with featured-artist residents (Spec: City
// Life). Data-driven: models/avatars/roster.json is the MODEL SET (which artists
// have a rigged body + where they stand); /api/featured-artists/showcase
// (is_featured_artist) supplies live names/genres and validates the roster.
//
// A featured artist with no roster entry is logged as "needs a model" so the
// content pipeline knows what to build next. Roster JSON is fetched SAME-ORIGIN
// (a Vite public asset), not through the API base.
export default function NpcManager() {
  const [npcs, setNpcs] = useState([]);
  const setNpcList = useWorldStore((s) => s.setNpcList);
  const setFollowing = useWorldStore((s) => s.setFollowing);
  const setOwned = useWorldStore((s) => s.setOwned);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // no-store: on returning to the street this re-reads the CURRENT file
      // (autosaved sizes/positions), not a stale browser-cached copy.
      const roster = await fetch('/models/avatars/roster.json', { cache: 'no-store' })
        .then((r) => r.json())
        .catch(() => ({ npcs: [] }));
      let featured = [];
      try { featured = (await api.featuredArtists())?.artists || []; } catch { /* offline → manifest only */ }

      // Who the viewer follows + owns (for the map's headshot pins). Optional —
      // a signed-out / offline viewer just gets no markers.
      try {
        const follows = (await api.following())?.following || [];
        if (!cancelled) setFollowing(follows.map((f) => f.avatar_id));
      } catch { if (!cancelled) setFollowing([]); }
      try {
        const mine = (await api.myAvatars())?.avatars || [];
        if (!cancelled) setOwned(mine.map((a) => a.avatar_id));
      } catch { if (!cancelled) setOwned([]); }

      const byId = new Map(featured.map((a) => [a.avatar_id, a]));
      const entries = roster.npcs || [];
      const modeledIds = new Set(entries.map((n) => n.avatarId));

      // Enrich each modeled NPC with live name/genre + headshot from the
      // featured list (headshot powers the map's follow markers).
      const merged = entries.map((n) => {
        const a = byId.get(n.avatarId);
        return {
          ...n,
          name: a?.name || n.name || `Artist ${n.avatarId}`,
          genre: a?.main_genre || null,
          headshotUrl: a?.profile_image_url || null,
        };
      });

      // Surface featured artists still waiting on a rigged model.
      const needsModel = featured.filter((a) => !modeledIds.has(a.avatar_id));
      if (needsModel.length) {
        console.log(`[NPC] ${merged.length} modeled · ${needsModel.length} featured artist(s) need a model:`,
          needsModel.map((a) => `${a.name} (#${a.avatar_id})`).join(', '));
      }
      if (!cancelled) { setNpcs(merged); setNpcList(merged); }
    })();
    return () => { cancelled = true; };
  }, [setNpcList]);

  if (!npcs.length) return null;
  return (
    <Suspense fallback={null}>
      {npcs.map((n) => <Npc key={n.avatarId ?? n.slug} npc={n} />)}
    </Suspense>
  );
}
